import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { InvitationOutcome } from '../lib/admin-invitations';
import { absolute } from '../lib/time';
import { CopySnippet } from './CopySnippet';
import { Modal } from './Modal';
import { Button } from './ui/Button';

// What an invitation row's write did, said once (plan 101 slice 19, row 19-10).
// Presenter: `AdminInvitationsPanel` holds the outcome and drops it on dismiss.
//
// A RESEND opens a dialog with the fresh accept link, like the invite dialog's
// result (`AdminInviteDialog`): the resend re-armed the invitation, so the link
// it answered is the ONLY live one, and the list read structurally carries no
// token (`InvitationView`). A toast would expire while its owner switches windows.
// It says the mail was sent only when the answer's `mailed` is true (03-13).
//
// A REVOKE that answered `revoked: false` found the invitation already revoked;
// that is not the act the operator confirmed, so it is a notice of its own. A
// revoke that landed needs nothing: the re-read shows the row's new status.

export function AdminInvitationOutcome(props: {
  readonly outcome: InvitationOutcome | null;
  readonly onDismiss: () => void;
}): JSX.Element {
  const resent = () => (props.outcome?.kind === 'resent' ? props.outcome.answer : null);
  const alreadyRevoked = () =>
    props.outcome?.kind === 'revoked' && !props.outcome.answer.revoked
      ? props.outcome.answer
      : null;

  return (
    <>
      <Show when={alreadyRevoked()}>
        {(answer) => (
          <div class="alert" role="status">
            <p class="alert__desc">
              {t('admin.invitation_already_revoked', { at: absolute(answer().revoked_at_iso) })}
            </p>
            <Button variant="outline" size="sm" onClick={props.onDismiss}>
              {t('admin.notice_dismiss')}
            </Button>
          </div>
        )}
      </Show>
      <Modal
        open={resent() !== null}
        title={t(
          resent()?.mailed === false ? 'admin.invitation_relinked' : 'admin.invitation_resent',
          {
            email: resent()?.email ?? '',
          },
        )}
        onClose={props.onDismiss}
        footer={<Button onClick={props.onDismiss}>{t('admin.access_done')}</Button>}
      >
        <Show when={resent()}>
          {(answer) => (
            <div class="field-group">
              <p role="status">
                {t(
                  answer().mailed
                    ? 'admin.invitation_resent_desc'
                    : 'admin.invitation_relinked_desc',
                )}
              </p>
              <CopySnippet label={t('admin.access_accept_link')} snippet={answer().accept_url} />
              <p class="field__desc">
                {t('admin.access_invited_expires', { at: absolute(answer().expires_at_iso) })}
              </p>
            </div>
          )}
        </Show>
      </Modal>
    </>
  );
}
